import { AppSettings } from "@/AppSettings";
import { SourceDataPriority, SourceDataReview, SourceDataVerification, UpdateSourceDataItemState } from "@/api/dtos";
import { IReviewAndVerificationItemData } from "@/components/forms/ReviewAndVerificationForm";
import ServiceStackClientContext from "@/contexts/api/ServiceStackClientContext";
import useItem from "@/hooks/form/source-data/useItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LoadingButton } from "@mui/lab";
import { Box, Tooltip, TooltipProps, styled } from "@mui/material";
import { useScopedField } from "@ngt/forms-core";
import { FC, useCallback, useContext, useMemo } from "react";
import { ButtonPlaceholder, CounterButton, CounterButtonBox, StyledTooltip } from ".";



const SourceDataFormReviewAndVerificationButton: FC<{ 
    itemData?: ReturnType<typeof useItem>;
    priority?: SourceDataPriority;
    hideReview?: boolean;
    hideVerification?: boolean;
    allowReview?: boolean;
    allowVerification?: boolean;
    itemGroupAllowReview?: boolean;
    itemGroupAllowVerification?: boolean;
}> = ({
    itemData,
    priority,
    hideReview,
    hideVerification,
    allowReview,
    allowVerification,
    itemGroupAllowReview,
    itemGroupAllowVerification
}) => {
    const propertyPath = useMemo(() => {
        if (!itemData?.studyEventOid ||
            !itemData?.studyEventRepeat ||
            !itemData?.caseReportFormOid ||
            !itemData?.itemGroupRepeat ||
            !itemData?.itemName) {
            return 'data.unknown'
        }

        return `data.${itemData.studyEventOid}.${itemData.studyEventRepeat}.${itemData.caseReportFormOid}.${itemData?.itemGroupName ?? AppSettings.metadata.itemGroupUngroupedKey}.${itemData.itemGroupRepeat}.${itemData.itemName}`;
    }, [
        // itemData?.trial?.id,
        // itemData?.masterGroup?.id,
        // itemData?.coordinatingGroup?.id,
        // itemData?.institution?.id,
        // itemData?.participant?.id,
        itemData?.studyEventOid,
        itemData?.studyEventRepeat,
        itemData?.caseReportFormOid,
        itemData?.itemGroupName,
        itemData?.itemGroupRepeat,
        itemData?.itemName
    ])

    const { 
        state: {
            value: data 
        },
        context: {
            setValue: setData,
            getValue: getData
        }
    } = useScopedField<IReviewAndVerificationItemData>(propertyPath, { value: true });

    const { 
        state: {
            value: loading 
        } 
    } = useScopedField<boolean>('loading', { value: true });

    const disabled = !itemData?.sourceDataItem;

    const reviewLoading = data?.reviewLoading
    const verificationsLoading = data?.verificationLoading

    const client = useContext(ServiceStackClientContext);

    const onReviewClick = useCallback(async () => {

        const current = getData();
        const newValue = !current?.review?.reviewed;
        const changeVerified = !!itemData?.configuration?.allowVerification && !newValue && current?.verification?.verified;
        const verificationValue = itemData?.configuration?.allowVerification ?
            changeVerified ?
                newValue :
                current?.verification?.verified:
            undefined

        setData(c => {
            c.reviewLoading = true;

            if (changeVerified) {
                c.verificationLoading = true;
            }

            return c;
        }, true, true, true, true);

        try {
            const response = await client.post(new UpdateSourceDataItemState({
                trialId: itemData?.trial?.id,
                masterGroupId: itemData?.masterGroup?.id,
                coordinatingGroupId: itemData?.coordinatingGroup?.id,
                institutionId: itemData?.institution?.id,
                participantId: itemData?.participant?.id,
                studyEventOid: itemData?.studyEventOid,
                studyEventRepeat: itemData?.studyEventRepeat,
                caseReportFormOid: itemData?.caseReportFormOid,
                caseReportFormVersionOid: itemData?.caseReportFormVersionOid,
                itemGroupName: itemData?.itemGroupName,
                itemGroupRepeat: itemData?.itemGroupRepeat,
                itemName: itemData?.itemName,
                value: itemData?.sourceDataItem?.value,
                priority: priority,
                reviewed: newValue,
                verified: verificationValue
            }));

            setData(c => {
                c.review = new SourceDataReview({
                    ...c.review,
                    reviewed: newValue
                });

                c.reviewLoading = false;

                if (changeVerified) {
                    c.verification = new SourceDataVerification({
                        ...c.verification,
                        verified: newValue
                    });

                    c.verificationLoading = false;
                }

                return c;
            }, true, true, true, true);
        }
        catch {
            setData(c => {
                c.reviewLoading = false;

                if (changeVerified) {
                    c.verificationLoading = false;
                }

                return c;
            }, true, true, true, true);
        }

    }, [getData, setData, client, priority, itemData?.configuration?.allowVerification]);


    

    const onVerifyClick = useCallback(async () => {
        const current = getData();
        const newValue = !current?.verification?.verified;
        const changeReview = !!itemData?.configuration?.allowReview && newValue && !current?.review?.reviewed;
        const reviewValue = itemData?.configuration?.allowVerification ?
            changeReview ?
                newValue :
                current?.review?.reviewed:
            undefined

        setData(c => {
            c.verificationLoading = true;

            if (changeReview) {
                c.reviewLoading = true;
            }

            return c;
        }, true, true, true, true);

        try {
            const response = await client.post(new UpdateSourceDataItemState({
                trialId: itemData?.trial?.id,
                masterGroupId: itemData?.masterGroup?.id,
                coordinatingGroupId: itemData?.coordinatingGroup?.id,
                institutionId: itemData?.institution?.id,
                participantId: itemData?.participant?.id,
                studyEventOid: itemData?.studyEventOid,
                studyEventRepeat: itemData?.studyEventRepeat,
                caseReportFormOid: itemData?.caseReportFormOid,
                caseReportFormVersionOid: itemData?.caseReportFormVersionOid,
                itemGroupName: itemData?.itemGroupName,
                itemGroupRepeat: itemData?.itemGroupRepeat,
                itemName: itemData?.itemName,
                value: itemData?.sourceDataItem?.value,
                priority: priority,
                verified: newValue,
                reviewed: reviewValue
            }));

            setData(c => {
                c.verification = new SourceDataVerification({
                    ...c.verification,
                    verified: newValue
                });

                c.verificationLoading = false;
    
                if (changeReview) {
                    c.review = new SourceDataReview({
                        ...c.review,
                        reviewed: newValue
                    });

                    c.reviewLoading = false;
                }

                return c;
            }, true, true, true, true);
        }
        catch {
            setData(c => {
                c.verificationLoading = false;
    
                if (changeReview) {
                    c.reviewLoading = false;
                }

                return c;
            }, true, true, true, true);
        }

    }, [getData, setData, client, priority, itemData?.configuration?.allowReview]);

    // useEffect(() => {
    //     setReviewed(itemData?.review?.reviewed);
    // }, [itemData?.review, setReviewed])
    
    // useEffect(() => {
    //     setVerified(itemData?.verification?.verified);
    // }, [itemData?.verification, setVerified])

    return (
        <>
            {
                !hideReview && allowReview ?
                    itemData?.configuration?.allowReview && itemGroupAllowReview ?
                        <Box>
                            <StyledTooltip
                                title={
                                    disabled ? 
                                        'Entry missing from OpenClinica' :
                                        data?.review?.reviewed ? 
                                            'Reviewed' : 
                                            'Not Reviewed'
                                }
                                followCursor
                            >
                                <CounterButtonBox>
                                    <CounterButton
                                        variant={
                                            data?.review?.reviewed ?
                                                'contained' :
                                                'outlined'
                                        }
                                        color={
                                            data?.review?.reviewed ?
                                                'success' :
                                                'primary'
                                        }
                                        loading={!!reviewLoading || !!loading}
                                        disabled={disabled}
                                        onClick={onReviewClick}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.review} size="2x" />
                                    </CounterButton>
                                </CounterButtonBox>
                            </StyledTooltip>
                        </Box>:
                        <ButtonPlaceholder /> :
                    null
            }
            {
                !hideVerification && allowVerification ?
                    itemData?.configuration?.allowVerification && itemGroupAllowVerification ?
                        <Box>
                            <StyledTooltip
                                title={
                                    disabled ? 
                                        'Entry missing from OpenClinica' :
                                        data?.verification?.verified ? 
                                            'Verified' : 
                                            'Not Verified'
                                }
                                followCursor
                            >
                                <CounterButtonBox>
                                    <CounterButton
                                        variant={
                                            data?.verification?.verified ?
                                                'contained' :
                                                'outlined'
                                        }
                                        color={
                                            data?.verification?.verified ?
                                                'success' :
                                                'primary'
                                        }
                                        loading={!!verificationsLoading || !!loading}
                                        onClick={onVerifyClick}
                                        disabled={disabled}
                                    >
                                        <FontAwesomeIcon icon={AppSettings.icons.verification} size="2x" />
                                    </CounterButton>
                                </CounterButtonBox>
                            </StyledTooltip>
                        </Box> :
                        <ButtonPlaceholder /> :
                    null
            }
        </>
    );
};

export default SourceDataFormReviewAndVerificationButton;